import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import * as XLSX from 'xlsx';

declare var require: any;
const FileSaver = require('file-saver');
const docx = require('docx-preview/dist/docx-preview.js');

@Component({
  selector: 'app-img-preview',
  templateUrl: './img-preview.component.html',
  styleUrls: ['./img-preview.component.css']
})
export class ImgPreviewComponent implements OnInit, OnDestroy {
  url: string = '';
  fileName: string = '';
  fileType: 'image' | 'pdf' | 'excel' | 'csv' | 'docx' | 'other' = 'other';
  safeUrl!: SafeResourceUrl;

  // New states for document previewing
  loading: boolean = false;
  errorMsg: string = '';
  excelSheets: Array<{ name: string; data: any[][] }> = [];
  activeSheetIndex: number = 0;
  // excelViewMode: 'office' | 'google' | 'table' = 'table';
  excelViewMode: any = 'table';
  safeOfficeUrl!: SafeResourceUrl;
  safeGoogleUrl!: SafeResourceUrl;
  isPublicExcelUrl: boolean = false;
  private createdBlobUrls: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log("preview component data: ", this.data);
    if (this.data) {
      this.url = this.data.url || '';

      if (this.data.fileName) {
        this.fileName = this.data.fileName;
      } else {
        this.fileName = this.getFileNameFromUrl(this.url);
      }

      this.fileType = this.getFileType(this.url, this.fileName);

      if (this.fileType === 'pdf') {
        this.loadPdfPreview();
      } else if (this.fileType === 'excel' || this.fileType === 'csv') {
        this.loadExcelPreview();
      } else if (this.fileType === 'docx') {
        this.loadDocxPreview();
      }
    }
  }

  getFileNameFromUrl(url: string): string {
    if (!url) return 'Document';
    if (url.startsWith('data:')) {
      const mime = url.split(';')[0].split(':')[1];
      const ext = mime ? mime.split('/')[1] : 'bin';
      return `document.${ext}`;
    }
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/');
    return parts[parts.length - 1] || 'Document';
  }

  getFileType(url: string, fileName?: string): 'image' | 'pdf' | 'excel' | 'csv' | 'docx' | 'other' {
    if (!url) return 'other';

    if (url.startsWith('data:')) {
      const mime = url.split(';')[0].split(':')[1];
      if (mime) {
        if (mime.startsWith('image/')) {
          return 'image';
        } else if (mime === 'application/pdf') {
          return 'pdf';
        } else if (
          mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          mime === 'application/vnd.ms-excel'
        ) {
          return 'excel';
        } else if (mime === 'text/csv') {
          return 'csv';
        } else if (
          mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          mime === 'application/msword'
        ) {
          return 'docx';
        }
      }
      return 'other';
    }

    const checkExt = (str?: string): string | null => {
      if (!str) return null;
      const clean = str.toLowerCase().split('?')[0].split('#')[0];
      const parts = clean.split('.');
      return parts.length > 1 ? parts.pop() || null : null;
    };

    const extension = checkExt(url) || checkExt(fileName);
    if (!extension) return 'other';

    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension)) {
      return 'image';
    } else if (extension === 'pdf') {
      return 'pdf';
    } else if (['xlsx', 'xls'].includes(extension)) {
      return 'excel';
    } else if (extension === 'csv') {
      return 'csv';
    } else if (['docx', 'doc'].includes(extension)) {
      return 'docx';
    }
    return 'other';
  }

  ngOnDestroy(): void {
    if (this.createdBlobUrls && this.createdBlobUrls.length > 0) {
      this.createdBlobUrls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) { }
      });
    }
  }

  loadPdfPreview() {
    this.loading = true;
    this.errorMsg = '';

    if (!this.url) {
      this.loading = false;
      this.errorMsg = 'No PDF file URL provided.';
      return;
    }

    if (this.url.startsWith('data:')) {
      try {
        const blob = this.getBlobFromDataUrl(this.url);
        const blobUrl = URL.createObjectURL(blob);
        this.createdBlobUrls.push(blobUrl);
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
        this.loading = false;
      } catch (e) {
        console.error(e);
        this.loading = false;
        this.errorMsg = 'Failed to parse PDF file.';
      }
    } else if (this.url.startsWith('blob:')) {
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
      this.loading = false;
    } else if (this.isPublicUrl(this.url)) {
      let targetUrl = this.url;
      if (targetUrl.startsWith('http://') && !targetUrl.includes('localhost') && !targetUrl.includes('127.0.0.1')) {
        targetUrl = targetUrl.replace(/^http:\/\//i, 'https://');
      }
      const googleViewerUrl = 'https://docs.google.com/viewer?url=' + encodeURIComponent(targetUrl) + '&embedded=true';
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(googleViewerUrl);
      this.loading = false;
    } else {
      let finalUrl = this.url;
      if (typeof window !== 'undefined' && window.location.protocol === 'https:' && finalUrl.startsWith('http://')) {
        finalUrl = finalUrl.replace(/^http:\/\//i, 'https://');
      }
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);
      this.loading = false;
    }
  }

  openInNewTab(): void {
    if (!this.url) return;
    if (this.url.startsWith('data:') || this.url.startsWith('blob:')) {
      const win = window.open();
      if (win) {
        win.document.write(`<iframe src="${this.url}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`);
      }
    } else {
      window.open(this.url, '_blank');
    }
  }

  getBlobFromDataUrl(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/pdf';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  getArrayBufferFromDataUrl(dataUrl: string): ArrayBuffer {
    const base64 = dataUrl.split(',')[1];
    const binaryStr = atob(base64);
    const len = binaryStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes.buffer;
  }

  hasCustomPortOrHttp: boolean = false;

  isPublicUrl(urlStr: string): boolean {
    if (!urlStr || urlStr.startsWith('data:') || urlStr.startsWith('blob:')) return false;
    let checkUrl = urlStr;
    if (checkUrl.startsWith('http://') && !checkUrl.includes('localhost') && !checkUrl.includes('127.0.0.1')) {
      checkUrl = checkUrl.replace(/^http:\/\//i, 'https://');
    }
    const lower = checkUrl.toLowerCase();
    if (lower.includes('localhost') || lower.includes('127.0.0.1')) {
      return false;
    }

    try {
      const parsedUrl = new URL(checkUrl);
      // Microsoft and Google Online Viewers strictly reject custom ports like :9309
      if (parsedUrl.port && parsedUrl.port !== '80' && parsedUrl.port !== '443') {
        this.hasCustomPortOrHttp = true;
        return false;
      }
      if (parsedUrl.protocol === 'http:') {
        this.hasCustomPortOrHttp = true;
        return false;
      }
    } catch (e) { }

    return lower.startsWith('https://');
  }

  setExcelViewMode(mode: 'office' | 'google' | 'table'): void {
    this.excelViewMode = mode;
  }

  loadExcelPreview() {
    this.loading = true;
    this.errorMsg = '';
    this.excelSheets = [];

    this.isPublicExcelUrl = this.isPublicUrl(this.url);
    if (this.isPublicExcelUrl) {
      // Use https for file URL if possible to avoid mixed content issues with online viewers
      let targetUrl = this.url;
      if (targetUrl.startsWith('http://') && !targetUrl.includes('localhost') && !targetUrl.includes('127.0.0.1')) {
        targetUrl = targetUrl.replace('http://', 'https://');
      }

      const officeUrl = 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(targetUrl);
      this.safeOfficeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(officeUrl);

      const googleUrl = 'https://docs.google.com/viewer?url=' + encodeURIComponent(targetUrl) + '&embedded=true';
      this.safeGoogleUrl = this.sanitizer.bypassSecurityTrustResourceUrl(googleUrl);
    }
    // Default to 'table' mode so the preview modal always opens instantly and reliably
    this.excelViewMode = 'table';

    if (this.url.startsWith('data:')) {
      try {
        const arrayBuffer = this.getArrayBufferFromDataUrl(this.url);
        this.parseExcel(arrayBuffer);
      } catch (e) {
        console.error(e);
        this.loading = false;
        this.errorMsg = 'Failed to parse spreadsheet file.';
      }
    } else {
      this.http.get(this.url, { responseType: 'arraybuffer' }).subscribe(
        (data: ArrayBuffer) => {
          this.parseExcel(data);
        },
        (error) => {
          console.error('Failed to load excel file via HTTP', error);
          if (!this.isPublicExcelUrl) {
            this.loading = false;
            this.errorMsg = 'Failed to fetch spreadsheet file.';
          } else {
            // Even if client CORS blocks arrayBuffer fetch, Office Web Viewer iframe may still work
            this.loading = false;
          }
        }
      );
    }
  }

  parseExcel(data: ArrayBuffer) {
    try {
      const workbook = XLSX.read(data, { type: 'array' });
      this.excelSheets = [];
      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];
        if (rawData.length > 0) {
          this.excelSheets.push({
            name: sheetName,
            data: rawData
          });
        }
      });

      if (this.excelSheets.length > 0) {
        this.activeSheetIndex = 0;
      } else {
        this.errorMsg = 'Spreadsheet is empty.';
      }
      this.loading = false;
    } catch (e) {
      console.error('SheetJS parse error', e);
      this.loading = false;
      this.errorMsg = 'Failed to render spreadsheet.';
    }
  }

  loadDocxPreview() {
    this.loading = true;
    this.errorMsg = '';

    const prepareAndRender = (arrayBuffer: ArrayBuffer) => {
      this.loading = false;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.renderDocx(arrayBuffer);
      }, 50);
    };

    if (this.url.startsWith('data:')) {
      try {
        const arrayBuffer = this.getArrayBufferFromDataUrl(this.url);
        prepareAndRender(arrayBuffer);
      } catch (e) {
        console.error(e);
        this.loading = false;
        this.errorMsg = 'Failed to parse document file.';
        this.cdr.detectChanges();
      }
    } else {
      this.http.get(this.url, { responseType: 'arraybuffer' }).subscribe(
        (data: ArrayBuffer) => {
          prepareAndRender(data);
        },
        (error) => {
          console.error('Failed to load word document', error);
          this.loading = false;
          this.errorMsg = 'Failed to fetch Word document.';
          this.cdr.detectChanges();
        }
      );
    }
  }

  renderDocx(data: ArrayBuffer) {
    this.cdr.detectChanges();
    const docxContainer = document.getElementById('docx-preview-container');
    if (!docxContainer) {
      console.warn('docx-preview-container not found in DOM');
      this.errorMsg = 'Preview container not ready.';
      this.cdr.detectChanges();
      return;
    }

    docxContainer.innerHTML = '';
    docx.renderAsync(data, docxContainer)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch((err: any) => {
        console.error('docx-preview render error', err);
        this.errorMsg = 'Failed to render Word document structure.';
        this.cdr.detectChanges();
      });
  }

  download() {
    if (!this.url) return;

    if (this.url.startsWith('data:') || this.url.startsWith('blob:')) {
      FileSaver.saveAs(this.url, this.fileName);
    } else {
      try {
        const link = document.createElement('a');
        link.href = this.url;
        link.target = '_blank';
        link.download = this.fileName || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        FileSaver.saveAs(this.url, this.fileName);
      }
    }
  }
}
